/**
 * Wishlist (Swym) integration — Horizon-native.
 *
 * The Swym Wishlist app (loaded via its store-level app embed) provides the SDK and binds to
 * the [data-swaction="addToWishlist"] buttons rendered by snippets/add-to-wishlist-button.liquid.
 * This module hooks Swym's callback queue to reproduce the reference theme's behavior:
 *   1. Seed + keep the header counter (#wishlist-item-count) live as items are saved/removed.
 *   2. Open the Horizon cart drawer when an item is added to the cart from the Swym wishlist UI.
 *
 * Written to be resilient across Swym SDK generations (the symbolic JSEvents names differ between
 * versions), since the live store's SDK version can't be assumed. Reimplemented for Horizon — no
 * reference code is copied. If the Swym app embed is not enabled, SwymCallbacks never fires and
 * this module is an inert no-op.
 *
 * LOADED AS A MODULE (`type="module"` in layout/theme.liquid) — deliberately. As a classic script
 * this used dynamic `import('@theme/cart-events')`, and Shopify's asset minification pipeline
 * transpiled that into `__toESM(require(...))` — CommonJS, which does not exist in the browser, so
 * the call threw into a silent catch and the drawer opened on a stale cart. A static import in a
 * real module survives the pipeline. The import map in snippets/scripts.liquid renders earlier in
 * <head> (theme.liquid:50 vs :91), so the bare specifier resolves.
 */
import { announceCartLinesUpdate } from '@theme/cart-events';

(function () {
  function counterEl() {
    return document.getElementById('wishlist-item-count');
  }

  function setCount(n) {
    var el = counterEl();
    if (!el) return;
    var v = Math.max(0, parseInt(n, 10) || 0);
    // Live parity: the badge always shows a number — "0" included.
    el.textContent = String(v);
  }

  function bumpCount(delta) {
    var el = counterEl();
    if (!el) return;
    setCount((parseInt(el.textContent, 10) || 0) + delta);
  }

  function openCartDrawer() {
    // Horizon 4.0 moved the dialog lifecycle off <cart-drawer-component> and onto the
    // <theme-drawer> host, which now owns the only public open(). Querying the old element found
    // it but `typeof drawer.open === 'function'` was always false, and the trigger fallback
    // searched inside the drawer for a button that lives in the header — so a Swym add-to-cart
    // silently never opened the drawer. Mirrors assets/standard-actions-override.js.
    var drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    // Fallback for a not-yet-upgraded custom element: click the real trigger in the header.
    // Deliberately no /cart redirect — navigating away on a wishlist add-to-cart is worse than
    // doing nothing.
    var trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) trigger.click();
  }

  /**
   * Swym adds to the cart with its OWN `POST /cart/add.js`, bypassing every theme cart event —
   * so just opening the drawer showed its stale server-rendered contents: the add succeeded
   * (cart.js item_count moved), but the drawer opened on the EMPTY state and the header bubble
   * stayed at 0. Announcing the mutation through the theme's cart event makes
   * `component-cart-items` re-render (with no `sections` payload it falls back to a fresh
   * Section Rendering fetch) and `cart-icon` re-read the cart for the bubble.
   *
   */
  function announceCartAdd() {
    try {
      announceCartLinesUpdate({ action: 'add', source: 'wishlist', sourceId: 'wishlist' });
    } catch (e) {
      /* The drawer opens with whatever it had — never let the announce break the add. */
    }
  }

  function refreshCartAndOpenDrawer() {
    openCartDrawer();

    /* Swym fires this event BEFORE its own add.js XHR settles — measured on the wishlist page:
       `sw:addedtocart` at t=735ms, the 200 response at t=745ms. Announcing immediately made the
       drawer re-render the PRE-add cart, which looked identical to the original bug. So: snapshot
       the cart, poll briefly until item_count moves (the add landing), then announce; announce at
       the timeout anyway so an already-settled add (other SDK generations fire after completion,
       and then the count never changes) still refreshes at worst ~1.5s late. */
    var announced = false;
    var announceOnce = function () {
      if (announced) return;
      announced = true;
      announceCartAdd();
    };

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (before) {
        var tries = 0;
        var tick = function () {
          tries += 1;
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (now) {
              if (now.item_count !== before.item_count || tries >= 5) announceOnce();
              else setTimeout(tick, 250);
            })
            .catch(announceOnce);
        };
        setTimeout(tick, 200);
      })
      .catch(announceOnce);
  }

  function seedCount(swat) {
    // Newer SDK: sum item counts across the customer's saved lists.
    if (typeof swat.fetchLists === 'function') {
      try {
        swat.fetchLists({
          callbackFn: function (lists) {
            var total = 0;
            (lists || []).forEach(function (list) {
              if (typeof list.lic === 'number') total += list.lic;
              else if (list.listcontents) total += list.listcontents.length;
            });
            setCount(total);
          },
          errorFn: function () {},
        });
        return;
      } catch (e) {
        /* fall through to the older helper */
      }
    }
    // Older SDK helper (used by the reference theme).
    if (typeof swat.renderWishlistCount === 'function') {
      var el = counterEl();
      if (el) {
        try {
          swat.renderWishlistCount(el);
        } catch (e) {}
      }
    }
  }

  /**
   * Re-bind the card hearts after a Section Rendering update.
   *
   * Filtering, sorting, paginating and searching all swap in fresh server markup for the product
   * cards. Swym's stylesheet keeps every `.swym-button` at `display: none` until its SDK has
   * processed the button (it then adds `swym-icon swym-heart swym-loaded` and an inline
   * `display: inline-block`), so the new markup renders with no heart at all — 0 of 10 cards on a
   * filtered collection, and it never recovers. Swym normally re-scans AJAX-loaded grids from a
   * MutationObserver, but that path is gated on the app's `ProductGridButtonConfig`, which this
   * store does not have set (`_swat.retailerSettings.ProductGridButtonConfig` is undefined), so
   * nothing re-initializes the buttons. We do it on the standard update events instead.
   *
   * `initializeActionButtons` takes a CONTAINER selector and only looks at `[data-swaction]`
   * elements *inside* the first match — passing a button selector silently no-ops.
   */
  function reinitActionButtons() {
    var swat = window._swat;
    if (!swat || typeof swat.initializeActionButtons !== 'function') return;
    try {
      swat.initializeActionButtons('body');
    } catch (e) {}
  }

  var reinitTimer = null;
  function scheduleReinitActionButtons() {
    if (reinitTimer) clearTimeout(reinitTimer);
    reinitTimer = setTimeout(reinitActionButtons, 250);
  }

  // StandardEvents.collectionUpdate / searchUpdate from @shopify/events. These fire around the
  // section swap rather than strictly after it, so they are a hint, not the source of truth —
  // the observer below is what guarantees the new cards get processed.
  ['shopify:collection:update', 'shopify:search:update'].forEach(function (name) {
    document.addEventListener(name, scheduleReinitActionButtons);
  });

  // Watch for cards arriving from any flow (filter, sort, paginate, search, quick-add re-render).
  // Guarded on an unprocessed button existing, so the class/style changes the initializer makes
  // can't feed back into another pass. childList only — attribute changes are not observed.
  function observeForNewButtons() {
    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(function () {
      if (!document.querySelector('[data-swaction]:not(.swym-loaded)')) return;
      scheduleReinitActionButtons();
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeForNewButtons);
  } else {
    observeForNewButtons();
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    if (!swat) return;
    var layer = swat.evtLayer;
    var ev = swat.JSEvents || {};

    function on(key, handler) {
      if (layer && key && typeof layer.addEventListener === 'function') {
        try {
          layer.addEventListener(key, handler);
        } catch (e) {}
      }
    }

    seedCount(swat);

    // Keep the header counter live as items are saved/removed from the wishlist.
    on(ev.addedToWishlist, function () { bumpCount(1); });
    on(ev.removedFromWishlist, function () { bumpCount(-1); });

    // Open the Horizon cart drawer when added to cart from the Swym wishlist UI.
    // `sw:addedtocart` is the documented raw event; older SDKs expose it as JSEvents.UIAddedToCart.
    on(ev.addedToCart || 'sw:addedtocart', refreshCartAndOpenDrawer);
    if (ev.UIAddedToCart) on(ev.UIAddedToCart, refreshCartAndOpenDrawer);
  });
})();
